import { Stack } from '@mui/joy';
import { RowClassParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ElementFormField } from 'bom/components';
import { IBomPart, IElement } from 'bom/types';
import { AgGridTable } from 'core/components';
import NotificationView from 'core/components/Notification/NotificationView';
import { ApiStatus, NotificationPosition } from 'core/enums';
import {
  AgGridClientTableProvider,
  useAgGridClientTableContext,
  useFormContext,
} from 'core/states';
import { AgGridDataRow } from 'core/types';

import { getColumnDefs } from './columns';
import { ICopyMoveToElementRecord } from './types';
import { convertBomPartsToCopyMoveToElementRecords } from './utils';

interface IProps {
  jobId: number;
  bomParts: IBomPart[];
  setSubmitDisabled: (submitDisabled: boolean) => void;
}

export const Table = ({ jobId, bomParts, setSubmitDisabled }: IProps) => {
  const { errors } = useFormContext();

  const columnDefs = useMemo(() => getColumnDefs((errors as FieldErrors).componentData), [errors]);

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      floatingFilter: false,
      sortable: false,
      resizable: false,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const records: ICopyMoveToElementRecord[] = useMemo(() => {
    return convertBomPartsToCopyMoveToElementRecords(bomParts);
  }, [bomParts]);

  const getRowClass = useCallback((row: RowClassParams) => {
    if (!row.data.isValid) {
      return 'disabledRow';
    }

    return '';
  }, []);

  const shouldRowBeUpdated = useCallback((newRow: AgGridDataRow, existingRow: AgGridDataRow) => {
    return newRow?.isValid !== existingRow?.isValid;
  }, []);

  return (
    <AgGridClientTableProvider
      tableId="lineTable"
      entityType="Line"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={false}
      loadingPercentage={100}
      dataCurrentPage={1}
      rowHeight={60}
      getRowClass={getRowClass}
      shouldRowBeUpdated={shouldRowBeUpdated}
      sideBar={{}}
      hasTopActions={false}
      hasRowSelection={false}
    >
      <TableWithinProvider jobId={jobId} setSubmitDisabled={setSubmitDisabled} />
    </AgGridClientTableProvider>
  );
};

const TableWithinProvider = ({
  jobId,
  setSubmitDisabled,
}: {
  jobId: number;
  setSubmitDisabled: (submitDisabled: boolean) => void;
}) => {
  const [allRecordsInvalid, setAllRecordsInvalid] = useState(false);

  const { t } = useTranslation();

  const { rows, updateRows } = useAgGridClientTableContext();
  const { setValue } = useFormContext();

  useEffect(() => {
    setSubmitDisabled(allRecordsInvalid);
  }, [allRecordsInvalid, setSubmitDisabled]);

  const onChangeElement = (element: IElement) => {
    let updatedRecords = [];

    if (!element) {
      updatedRecords = rows.map((row, index) => {
        setValue(`componentData.${index}.isValid`, true);

        return {
          ...row,
          isValid: true,
        };
      });
    }

    if (element) {
      updatedRecords = rows.map((row, index) => {
        if (row.elements.some((rowElement: IElement) => rowElement.id === element?.id)) {
          setValue(`componentData.${index}.quantity`, null);
          setValue(`componentData.${index}.isValid`, false);

          return {
            ...row,
            quantity: null,
            isValid: false,
          };
        }

        setValue(`componentData.${index}.isValid`, true);

        return {
          ...row,
          isValid: true,
        };
      });
    }

    setAllRecordsInvalid(updatedRecords.every((row) => !row.isValid));

    updateRows({ update: updatedRecords }, { redrawRows: true });
  };

  const onSubmitSuccessAddNewElement = (element: Partial<IElement>) => setValue('element', element);

  return (
    <Stack sx={{ gap: 'inherit', height: '100%' }}>
      <NotificationView
        position={NotificationPosition.DRAWER}
        showNotification={allRecordsInvalid}
        title={t('titleCopyMoveNotAllowed')}
        message={t('messageCopyMoveNotAllowed')}
        type={ApiStatus.WARNING}
      />

      <ElementFormField
        jobId={jobId}
        name="element"
        label={t('labelElement')}
        placeholder={t('placeholderSelectElement')}
        requestPayload={{ filters: { bomId: jobId } }}
        onChange={onChangeElement}
        onSubmitSuccessAddNewElement={onSubmitSuccessAddNewElement}
        wrapperStyle={{ width: '50%' }}
      />

      <AgGridTable />
    </Stack>
  );
};
